import {
  CognitoResponse,
  DuplicationCheck,
  ErrorResponse,
  OnboardingDuplicationActionType,
  OnboardingDuplicationCheckType
} from '@cbgms/api'
import { POSTOnboardingLoginBody } from '@cbgms/api/sso/auth/onboarding'
import { UseMutationOptions, useMutation } from '@cbgms/base/http/use-query'
import { handleValidateWholesaler, setOnboardingToken } from 'app/auth/state/actions'
import { useDispatch } from 'react-redux'
import { useDuplicateCheckMGID, useDuplicateCheckOrderAccount, useValidateAxNumber } from 'app/auth/components/hooks/use-duplicate-checks'
import { useCountryFromUrl } from '@cbgms/base/hooks'

export interface UseWholesalerValidatorResult extends POSTOnboardingLoginBody {
  token: CognitoResponse
}

// useWholesalerValidatorBuilder builds a useMutation that can be used in your components
export const useWholesalerValidatorBuilder = (
  basePath: string,
  locale: string,
  wholesalerUUID: string,
  duplicationChecks: DuplicationCheck[],
  options:
    | UseMutationOptions<UseWholesalerValidatorResult, { response: { data: ErrorResponse } }, { [key: string]: string }, unknown>
    | undefined
) => {
  const dispatch = useDispatch()
  const { isNetherlands } = useCountryFromUrl()
  const handleDuplicates = useDuplicateCheckOrderAccount(duplicationChecks, basePath)
  const handleInvalidAxNumber = useValidateAxNumber(basePath)
  const mgidDuplicateCheck: DuplicationCheck[] = [
    {
      CheckType: OnboardingDuplicationCheckType.ShopMGID,
      Active: true,
      ActionOnDuplicate: OnboardingDuplicationActionType.RejectAndShowSupportDeskMessage
    }
  ]
  const handleMGIDDuplicates = useDuplicateCheckMGID(mgidDuplicateCheck, basePath)

  return useMutation(async (credentials: { [key: string]: string }) => {
    const wholeSalerCredentials = {
      PreferredLocale: locale,
      WholesalerUUID: wholesalerUUID,
      Credentials: credentials
    }
    const { Data } = await dispatch(handleValidateWholesaler(wholeSalerCredentials))

    if (Data?.AuthenticationResult?.IDToken) {
      // set a temporary token, so the next api requests have a token they can use.
      await dispatch(setOnboardingToken(Data.AuthenticationResult))
    }

    // Don't change the order of these invocations,
    // we want to run the AX number validation before the duplicate checks.
    if (isNetherlands) handleInvalidAxNumber()
    await handleDuplicates(Data.Credentials.UserId, wholesalerUUID)
    if (isNetherlands && credentials.ExternalID) {
      await handleMGIDDuplicates(credentials.ExternalID)
    }
    return { token: Data, ...wholeSalerCredentials, Credentials: Data.Credentials } as UseWholesalerValidatorResult
  }, options)
}
